import { MEDLEVE_DEFAULT_OG_IMAGE, MEDLEVE_OG_LOCALE, MEDLEVE_SITE_NAME } from './constants'
import { absoluteUrl, CanonicalOptions, canonicalUrl } from './urls'

export interface SeoImage {
  /** Site-relative path under `/public` or absolute URL. */
  path: string
  width?: number
  height?: number
  alt?: string
}

export interface SeoMetaInput {
  /** Full `<title>`, e.g. "Tratamento para Emagrecimento Online | Medleve". */
  title: string
  description: string
  /** Current path or URL (`router.asPath`, `req.url`); tracking params are stripped for the canonical. */
  path: string
  /** `false` emits `noindex, nofollow`. Default `true`. */
  index?: boolean
  ogType?: 'website' | 'article' | 'profile'
  image?: SeoImage
  canonicalOptions?: CanonicalOptions
}

export interface SeoMeta {
  title: string
  description: string
  canonical: string
  robots: string
  openGraph: {
    type: string
    url: string
    title: string
    description: string
    siteName: string
    locale: string
    image?: { url: string; width?: number; height?: number; alt?: string }
  }
  twitterCard: 'summary' | 'summary_large_image'
}

export const ROBOTS_INDEX = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
export const ROBOTS_NOINDEX = 'noindex, nofollow'

/** Pure metadata resolver for Medleve organic pages (PRD §34–36, §73). */
export const buildSeoMeta = ({
  title,
  description,
  path,
  index = true,
  ogType = 'website',
  image = MEDLEVE_DEFAULT_OG_IMAGE,
  canonicalOptions
}: SeoMetaInput): SeoMeta => {
  const canonical = canonicalUrl(path, canonicalOptions)

  return {
    title,
    description,
    canonical,
    robots: index ? ROBOTS_INDEX : ROBOTS_NOINDEX,
    openGraph: {
      type: ogType,
      url: canonical,
      title,
      description,
      siteName: MEDLEVE_SITE_NAME,
      locale: MEDLEVE_OG_LOCALE,
      image: image
        ? { url: absoluteUrl(image.path), width: image.width, height: image.height, alt: image.alt }
        : undefined
    },
    twitterCard: image ? 'summary_large_image' : 'summary'
  }
}
